import { useState } from 'react';
import { useMediaQuery } from '@skin-seoul/design-system';
import {
  useUser,
  transformOrderForDisplay,
  ORDER_QUERIES,
  useReviewService,
  type ReviewFormData,
} from '@skin-seoul/service';
import {
  OrderCard,
  OrderCardSkeleton,
  LucideIcon,
  ReviewWritePcModal,
  ReviewWriteMobileModal,
  type OrderItem,
} from '@skin-seoul/shared-components';
import { useCallbackRef } from '@skin-seoul/utils';
import { useQuery } from '@tanstack/react-query';

interface OrderItemProps {
  orderId: string;
  onClickWriteReview: (item: OrderItem) => void;
}

const OrderItemCard = ({ orderId, onClickWriteReview }: OrderItemProps) => {
  const { data: order, isLoading, isError } = useQuery(ORDER_QUERIES.getOrder({ orderId: Number(orderId) }));

  if (isLoading) {
    return <OrderCardSkeleton />;
  }

  if (isError || !order) {
    return null;
  }

  return (
    <OrderCard
      order={transformOrderForDisplay(order, { isRegistered: true })}
      onClickWriteReview={onClickWriteReview}
    />
  );
};

const RegisteredDeliveryTracking = () => {
  const { user, isLoading: isUserLoading, isAuthenticated } = useUser();
  const isPC = useMediaQuery('tablet');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);

  const { useCreateReview } = useReviewService();
  const { mutate: createReview } = useCreateReview();

  const { data: userOrders, isLoading: isOrdersLoading } = useQuery({
    ...ORDER_QUERIES.getUserOrders({ userId: Number(user?.userId ?? 0) }),
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  const handleSubmitReview = useCallbackRef((values: ReviewFormData) => {
    return new Promise<void>((resolve, reject) => {
      if (!selectedItem?.productId) {
        reject(new Error('Product ID is required'));
        return;
      }

      createReview(
        {
          name: values.nickname,
          rating: values.rating,
          body: values.content,
          productId: Number(selectedItem.productId),
          title: values.title,
          files: values.images,
          skinType: [values.skinType],
          skinConcern: [values.skinConcern],
          email: values.email,
        },
        {
          onSuccess: () => {
            resolve();
          },
          onError: (error) => {
            reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
          },
        },
      );
    });
  });

  const handleClickWriteReview = useCallbackRef((item: OrderItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  });

  const handleCloseModal = useCallbackRef(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  });

  if (isUserLoading || isOrdersLoading) {
    return (
      <div className="flex flex-col w-full gap-4 p-4">
        <OrderCardSkeleton />
        <OrderCardSkeleton />
      </div>
    );
  }

  const hasOrders = userOrders?.orderIds && userOrders.orderIds.length > 0;

  if (!hasOrders) {
    return (
      <div className="flex flex-col w-full items-center justify-center gap-3 rounded-[8px] border border-neutral-100 bg-generic-0 p-8 min-h-[200px]">
        <LucideIcon name="Box" size={16} className="text-neutral-400" />
        <h5 className="text-heading-h5-mobile font-semibold text-neutral-700">No Orders Found</h5>
        <p className="text-paragraph-medium text-neutral-400 text-center">You haven&apos;t placed any orders yet.</p>
      </div>
    );
  }

  const productInfo = selectedItem
    ? {
        name: selectedItem.name,
        brand: selectedItem.brand ?? '',
        image: selectedItem.imageUrl ?? '',
        category: selectedItem.category ?? '',
      }
    : null;

  return (
    <>
      <div className="flex flex-col w-full gap-4">
        <div className="flex flex-col w-full gap-2 items-center tablet:items-start">
          <h6 className="text-heading-h6-mobile font-medium text-neutral-900 text-center tablet:text-left">Orders</h6>
          <p className="text-paragraph-small font-normal text-neutral-400 text-center tablet:text-left">
            Check your order and delivery status.
          </p>
        </div>

        <div className="flex flex-col w-full gap-4">
          {userOrders.orderIds.map((orderId) => (
            <OrderItemCard key={orderId} orderId={orderId} onClickWriteReview={handleClickWriteReview} />
          ))}
        </div>
      </div>

      {productInfo &&
        (isPC ? (
          <ReviewWritePcModal
            isOpen={isModalOpen}
            productInfo={productInfo}
            productId={selectedItem?.productId ? Number(selectedItem.productId) : undefined}
            onClose={handleCloseModal}
            onSubmit={handleSubmitReview}
          />
        ) : (
          <ReviewWriteMobileModal
            isOpen={isModalOpen}
            productInfo={productInfo}
            productId={selectedItem?.productId ? Number(selectedItem.productId) : undefined}
            onClose={handleCloseModal}
            onSubmit={handleSubmitReview}
          />
        ))}
    </>
  );
};

export default RegisteredDeliveryTracking;
